/* eslint-disable unicorn/filename-case */
import { RadioGroup, RadioGroupIndicator, RadioGroupItem } from '@radix-ui/react-radio-group';
import { type useForm } from '@tanstack/react-form';
import { ArrowRight, HelpCircle, Stars02 } from '@untitled-ui/icons-react';
import { twMerge } from 'tailwind-merge';
import z from 'zod';

import { Badge } from '../../../components/badge';
import { ErrorField } from '../../../components/inputs/error-field';
import { TextArea } from '../../../components/inputs/text-area';
import { TextField } from '../../../components/inputs/text-field';
import { SingleSelectPills } from '../../../components/select-pill';
import { Tooltip } from '../../../components/tooltip';
import { tGlobal, tNamespace } from '../../../services/translation';
import { parseFirstZodError } from '../../../shared/validation';

const t = tNamespace('app.blogs.-modal-rewrite');

type RewriteOptimizationFormProps = {
  form: ReturnType<
    typeof useForm<{
      country: string;
      language: string;
      chosenTitle: string;
      providedContentType: 'url' | 'text';
      url: string;
      tiptapJsonContent: Record<string, any> | null;
      primaryKeyword: string;
      primaryPrompt: string;
      mode: 'light' | 'major';
      contentLength: 'auto' | 'same' | 'custom';
      contentLengthValue: number;
      extraInformation: string;
    }>
  >;
};

export function RewriteOptimizationForm(props: RewriteOptimizationFormProps) {
  const { form } = props;

  const refreshTypeOptions = [
    {
      value: 'light',
      label: t('lightRefreshLabel'),
      description: t('lightRefreshDescription'),
      bullets: [
        t('lightRefreshBullet1'),
        t('lightRefreshBullet2'),
        t('lightRefreshBullet3'),
        t('lightRefreshBullet4'),
      ],
    },
    {
      value: 'major',
      label: t('majorRewriteLabel'),
      description: t('majorRewriteDescription'),
      bullets: [
        t('majorRewriteBullet1'),
        t('majorRewriteBullet2'),
        t('majorRewriteBullet3'),
        t('majorRewriteBullet4'),
      ],
      comingSoon: true,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Refresh Type Selection */}
      <form.Field
        name="mode"
        // eslint-disable-next-line react/no-children-prop
        children={function (field) {
          return (
            <RadioGroup
              className="grid grid-cols-2 gap-3"
              value={field.state.value}
              onValueChange={function (newValue) {
                field.handleChange(newValue as 'light' | 'major');
              }}
            >
              {refreshTypeOptions.map(function (option) {
                const isSelected = field.state.value === option.value;
                const isComingSoon = option.comingSoon;
                return (
                  <label
                    key={option.value}
                    className={twMerge(
                      'relative flex cursor-pointer flex-col gap-3 rounded-lg border border-branding-gray-200 bg-white p-4 transition-all hover:border-2 hover:border-branding-accent hover:bg-branding-accent-50 hover:p-[15px]',
                      isSelected &&
                        'border-2 border-branding-accent bg-branding-accent-50 p-[15px]',
                      isComingSoon &&
                        'cursor-not-allowed opacity-60 hover:border hover:border-branding-gray-200 hover:bg-white hover:p-4'
                    )}
                  >
                    {isComingSoon && (
                      <Badge size="sm" color="yellow" className="absolute -bottom-2 right-0">
                        {t('comingSoon')}
                      </Badge>
                    )}
                    <div className="flex items-start gap-3">
                      <RadioGroupItem
                        value={option.value}
                        disabled={isComingSoon}
                        className="group mt-0.5 size-5 shrink-0 rounded-full border border-branding-gray-300 bg-white shadow-1 hover:border-branding-accent hover:bg-branding-accent-50 focus:border-branding-accent-200 focus:outline-none focus:ring-4 focus:ring-branding-accent-200 data-[state=checked]:border-branding-accent data-[state=checked]:bg-branding-accent-50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-branding-gray-300 disabled:hover:bg-white"
                      >
                        <RadioGroupIndicator className="relative flex size-full items-center justify-center after:block after:size-2.5 after:rounded-full after:bg-branding-accent" />
                      </RadioGroupItem>
                      <div className="flex flex-1 flex-col gap-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-display-4 text-branding-black">{option.label}</span>
                          <span className="font-body-3-r text-branding-gray">
                            {option.description}
                          </span>
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>
          );
        }}
      />

      {/* Content Length Section */}
      <div className="flex flex-col overflow-hidden rounded-lg border border-branding-accent-200">
        <div className="bg-branding-accent-50 p-4">
          <div className="mx-4 grid grid-cols-2 gap-2">
            {refreshTypeOptions[0].bullets.map(function (bullet, index) {
              return (
                <div key={index} className="font-body-3-r flex items-center gap-2">
                  <ArrowRight className="shrink-0 text-branding-accent" width={12} height={12} />
                  <span>{bullet}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-1.5">
            <Stars02 className="size-4 text-branding-accent" />
            <span className="font-body-2-r bg-gradient-to-r from-branding-accent-600 to-purple-900 bg-clip-text text-transparent">
              {t('contentLengthLabel')}
            </span>
            <Tooltip text={t('contentLengthLabel')} description={t('contentLengthAutoDescription')}>
              <div>
                <HelpCircle width={12} height={12} className="text-branding-gray" />
              </div>
            </Tooltip>
          </div>

          <form.Field
            name="contentLength"
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <div
                  className={twMerge(
                    'flex items-center gap-2',
                    field.state.value === 'auto' && 'flex-col items-start'
                  )}
                >
                  <SingleSelectPills
                    items={[
                      { value: 'auto', label: t('contentLengthAuto') },
                      { value: 'same', label: t('contentLengthSame') },
                      { value: 'custom', label: t('contentLengthCustom') },
                    ]}
                    value={field.state.value}
                    onChange={function (value) {
                      field.handleChange(value as 'auto' | 'same' | 'custom');
                    }}
                    size="xs"
                    color="gray"
                    className="gap-3"
                  />

                  {/* Show description when Auto is selected */}
                  {field.state.value === 'auto' && (
                    <p className="font-body-3-r text-branding-gray">
                      {t('contentLengthAutoDescription')}
                    </p>
                  )}

                  {/* Show custom input when Custom is selected */}
                  {field.state.value === 'custom' && (
                    <form.Field
                      name="contentLengthValue"
                      validators={{
                        onChange: function (data) {
                          return parseFirstZodError(
                            z
                              .number()
                              .min(50, 'CONTENT_LENGTH_VALUE_MIN')
                              .max(5000, 'CONTENT_LENGTH_VALUE_MAX'),
                            data.value
                          );
                        },
                        onBlur: function (data) {
                          return parseFirstZodError(
                            z
                              .number()
                              .min(50, 'CONTENT_LENGTH_VALUE_MIN')
                              .max(5000, 'CONTENT_LENGTH_VALUE_MAX'),
                            data.value
                          );
                        },
                        onSubmit: function (data) {
                          return parseFirstZodError(
                            z
                              .number()
                              .min(50, 'CONTENT_LENGTH_VALUE_MIN')
                              .max(5000, 'CONTENT_LENGTH_VALUE_MAX'),
                            data.value
                          );
                        },
                      }}
                      // eslint-disable-next-line react/no-children-prop
                      children={function (wordCountField) {
                        return (
                          <div className="relative">
                            <div className="flex w-fit items-center">
                              <TextField
                                type="number"
                                value={
                                  wordCountField.state.value === 0 ? '' : wordCountField.state.value
                                }
                                onChange={function (e) {
                                  const { value } = e.target;
                                  wordCountField.handleChange(value === '' ? 0 : Number(value));
                                }}
                                sizeField="sm"
                                max={5000}
                                placeholder="1500"
                                containerClassName="w-24 max-w-24"
                                error={wordCountField.state.meta?.errors}
                                delegatedErrorMessage
                              />
                              <p className="font-body-2-r absolute left-28 top-[13.5%] text-black">
                                {tGlobal('words')}
                              </p>
                            </div>
                            <ErrorField
                              error={wordCountField.state.meta?.errors}
                              sizeField="sm"
                              className="absolute left-0 top-full mt-1 whitespace-nowrap"
                            />
                          </div>
                        );
                      }}
                    />
                  )}
                </div>
              );
            }}
          />

          {/* Specific Instructions */}
          <form.Field
            name="extraInformation"
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextArea
                  name={field.name}
                  size="md"
                  label={
                    <div className="flex items-center gap-1">
                      {t('specificInstructionsLabel')}
                      <span className="text-branding-gray">
                        {t('specificInstructionsOptional')}
                      </span>
                    </div>
                  }
                  placeholder={t('specificInstructionsPlaceholder')}
                  value={field.state.value}
                  onChange={function (e) {
                    field.handleChange(e.target.value);
                  }}
                  rows={4}
                />
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
